import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import {
    Box,
    Typography,
    Button,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Menu,
    MenuItem,
    TextField,
    InputAdornment,
    Chip,
    CircularProgress,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Snackbar,
    Stack
} from '@mui/material';
import {
    Add as AddIcon,
    Search as SearchIcon,
    MoreVert as MoreVertIcon,
    Edit as EditIcon,
    Visibility as VisibilityIcon,
    Delete as DeleteIcon
} from '@mui/icons-material';
import { useStore } from '../stores';
import { Vehicle } from '../stores/VehicleStore';
import DashboardLayout from '../components/layout/DashboardLayout/DashboardLayout';
import MotorcycleRegistrationForm from '../components/forms/MotorcycleRegistrationForm';
import VehicleEditForm from '../components/forms/VehicleEditForm';
import VehicleDetailView from '../components/views/VehicleDetailView';

const Vehicles: React.FC = observer(() => {
    const { vehicleStore } = useStore();
    const [searchTerm, setSearchTerm] = useState('');
    const [showCreateDialog, setShowCreateDialog] = useState(false);
    const [showEditDialog, setShowEditDialog] = useState(false);
    const [showViewDialog, setShowViewDialog] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [showSuccessMessage, setShowSuccessMessage] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [selectedVehicle, setSelectedVehicle] = useState<Vehicle | null>(null);
    const [filteredVehicles, setFilteredVehicles] = useState<Vehicle[]>([]);

    useEffect(() => {
        vehicleStore.loadVehicles();
    }, [vehicleStore]);

    useEffect(() => {
        if (searchTerm.trim() === '') {
            setFilteredVehicles(vehicleStore.vehicles);
        } else {
            const filtered = vehicleStore.vehicles.filter(vehicle =>
                vehicle.license_plate.toLowerCase().includes(searchTerm.toLowerCase()) ||
                vehicle.brand.toLowerCase().includes(searchTerm.toLowerCase()) ||
                vehicle.model.toLowerCase().includes(searchTerm.toLowerCase()) ||
                (vehicle.vin && vehicle.vin.toLowerCase().includes(searchTerm.toLowerCase()))
            );
            setFilteredVehicles(filtered);
        }
    }, [searchTerm, vehicleStore.vehicles]);

    const handleVehicleCreatedSuccess = () => {
        setShowCreateDialog(false);
        setSuccessMessage('Vehículo registrado exitosamente');
        setShowSuccessMessage(true);
        vehicleStore.loadVehicles();
    };

    const handleCloseSnackbar = () => {
        setShowSuccessMessage(false);
    };

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, vehicle: Vehicle) => {
        event.stopPropagation();
        
        setAnchorEl(event.currentTarget);
        setSelectedVehicle(vehicle);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setSelectedVehicle(null);
    };

    const handleEdit = () => {
        if (selectedVehicle) {
            setShowEditDialog(true);
            setAnchorEl(null);
        }
    };

    const handleView = () => {
        if (selectedVehicle) {
            setShowViewDialog(true);
            setAnchorEl(null);
        }
    };

    const handleDelete = () => {
        if (selectedVehicle) {
            setShowDeleteDialog(true);
            setAnchorEl(null);
        }
    };

    const handleVehicleEditSuccess = () => {
        setShowEditDialog(false);
        setSelectedVehicle(null);
        setSuccessMessage('Vehículo actualizado exitosamente');
        setShowSuccessMessage(true);
    };

    const handleEditDialogClose = () => {
        setShowEditDialog(false);
        setSelectedVehicle(null);
    };

    const handleConfirmDelete = async () => {
        if (selectedVehicle) {
            const success = await vehicleStore.deleteVehicle(selectedVehicle.id);
            if (success) {
                setShowDeleteDialog(false);
                setSelectedVehicle(null);
                setSuccessMessage('Vehículo eliminado exitosamente');
                setShowSuccessMessage(true);
            }
        }
    };

    const handleDeleteDialogClose = () => {
        setShowDeleteDialog(false);
        setSelectedVehicle(null);
    };

    const handleViewDialogClose = () => {
        setShowViewDialog(false);
        setSelectedVehicle(null);
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'ACTIVE':
                return 'success';
            case 'MAINTENANCE':
                return 'warning';
            case 'INACTIVE':
                return 'error';
            default:
                return 'default';
        }
    };

    const getStatusLabel = (status: string) => {
        switch (status) {
            case 'ACTIVE':
                return 'Activo';
            case 'MAINTENANCE':
                return 'En Mantenimiento';
            case 'INACTIVE':
                return 'Inactivo';
            default:
                return status;
        }
    };

    return (
        <DashboardLayout>
            <Box>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                    <Box>
                        <Typography variant="h4" fontWeight="500" mb={1}>
                            Gestión de Vehículos
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            Administra todos los vehículos registrados en el sistema
                        </Typography>
                    </Box>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => setShowCreateDialog(true)}
                        sx={{
                            backgroundColor: '#1A1A1A',
                            color: 'white',
                            borderRadius: 1.5,
                            px: 3,
                            py: 1.5,
                            textTransform: 'none',
                            fontWeight: 500,
                            '&:hover': {
                                backgroundColor: '#333333',
                            },
                        }}
                    >
                        Crear Vehículo
                    </Button>
                </Box>

                <Paper
                    elevation={0}
                    sx={{
                        borderRadius: 2,
                        border: '1px solid #e0e0e0',
                        overflow: 'hidden'
                    }}
                >
                    <Box p={3} borderBottom="1px solid #e0e0e0">
                        <TextField
                            fullWidth
                            placeholder="Buscar vehículos por placa, marca, modelo ..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon color="action" />
                                    </InputAdornment>
                                ),
                            }}
                            variant="outlined"
                            size="medium"
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                    backgroundColor: 'white',
                                }
                            }}
                        />
                    </Box>

                    {vehicleStore.error && (
                        <Box p={3}>
                            <Alert severity="error">
                                {vehicleStore.error}
                            </Alert>
                        </Box>
                    )}

                    <TableContainer>
                        {vehicleStore.isLoading ? (
                            <Box display="flex" justifyContent="center" py={8}>
                                <CircularProgress />
                            </Box>
                        ) : (
                            <Table>
                                <TableHead>
                                    <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                                        <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Placa</TableCell>
                                        <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Marca/Modelo</TableCell>
                                        <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Año</TableCell>
                                        <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Color</TableCell>
                                        <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Estado</TableCell>
                                        <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Serial Motor</TableCell>
                                        <TableCell align="center" sx={{ fontWeight: 600, color: '#374151' }}>Acciones</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredVehicles.map((vehicle) => (
                                        <TableRow
                                            key={vehicle.id}
                                            sx={{
                                                '&:hover': {
                                                    backgroundColor: '#f8f9fa'
                                                }
                                            }}
                                        >
                                            <TableCell>
                                                <Typography variant="body1" fontWeight={500}>
                                                    {vehicle.license_plate}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body1" fontWeight={500}>
                                                    {vehicle.brand}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    {vehicle.model}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">
                                                    {vehicle.year}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">
                                                    {vehicle.color}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={getStatusLabel(vehicle.status)}
                                                    color={getStatusColor(vehicle.status) as any}
                                                    size="small"
                                                    sx={{ fontWeight: 500 }}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                                    {vehicle.vin ? vehicle.vin.substring(0, 8) + '...' : 'N/A'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="center">
                                                <IconButton
                                                    onClick={(e) => handleMenuOpen(e, vehicle)}
                                                    size="small"
                                                >
                                                    <MoreVertIcon />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}

                        {filteredVehicles.length === 0 && !vehicleStore.isLoading && (
                            <Box textAlign="center" py={8}>
                                {searchTerm ? (
                                    <Typography variant="body1" color="text.secondary">
                                        No se encontraron vehículos con "{searchTerm}"
                                    </Typography>
                                ) : (
                                    <Stack alignItems="center" spacing={2}>
                                        <Typography variant="body1" color="text.secondary">
                                            No hay vehículos registrados
                                        </Typography>
                                        <Button
                                            variant="contained"
                                            startIcon={<AddIcon />}
                                            onClick={() => setShowCreateDialog(true)}
                                            sx={{
                                                backgroundColor: '#1A1A1A',
                                                '&:hover': {
                                                    backgroundColor: '#333333',
                                                },
                                            }}
                                        >
                                            Crear Primer Vehículo
                                        </Button>
                                    </Stack>
                                )}
                            </Box>
                        )}
                    </TableContainer>
                </Paper>
            </Box>
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                PaperProps={{
                    sx: {
                        boxShadow: '0px 5px 15px rgba(0, 0, 0, 0.1)',
                        borderRadius: 2,
                        minWidth: 180,
                    }
                }}
            >
                <MenuItem onClick={handleView} sx={{ py: 1 }}>
                    <VisibilityIcon sx={{ mr: 2, fontSize: 18 }} />
                    <Typography variant="body2">Ver detalles</Typography>
                </MenuItem>
                <MenuItem onClick={handleEdit} sx={{ py: 1 }}>
                    <EditIcon sx={{ mr: 2, fontSize: 18 }} />
                    <Typography variant="body2">Editar</Typography>
                </MenuItem>
                <MenuItem onClick={handleDelete} sx={{ py: 1, color: 'error.main' }}>
                    <DeleteIcon sx={{ mr: 2, fontSize: 18 }} />
                    <Typography variant="body2">Eliminar</Typography>
                </MenuItem>
            </Menu>
            <Dialog
                open={showCreateDialog}
                onClose={() => setShowCreateDialog(false)}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 2,
                    }
                }}
            >
                <MotorcycleRegistrationForm
                    onSuccess={handleVehicleCreatedSuccess}
                />
            </Dialog>
            <Dialog
                open={showEditDialog}
                onClose={handleEditDialogClose}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 2,
                    }
                }}
            >
                <DialogTitle sx={{ pb: 1 }}>
                    Editar Vehículo
                </DialogTitle>
                <DialogContent sx={{ px: 3, py: 2 }}>
                    {selectedVehicle && (
                        <VehicleEditForm
                            vehicle={selectedVehicle}
                            onSuccess={handleVehicleEditSuccess}
                            onCancel={handleEditDialogClose}
                        />
                    )}
                </DialogContent>
            </Dialog>

            <Dialog
                open={showViewDialog}
                onClose={handleViewDialogClose}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 2,
                    }
                }}
            >
                <DialogTitle sx={{ pb: 1 }}>
                    Detalles del Vehículo
                </DialogTitle>
                <DialogContent sx={{ px: 3, py: 2 }}>
                    {selectedVehicle && (
                        <VehicleDetailView vehicle={selectedVehicle} />
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button
                        onClick={handleViewDialogClose}
                        sx={{
                            borderRadius: 1.5,
                            px: 3,
                            py: 1.5,
                            textTransform: 'none',
                            fontWeight: 500,
                        }}
                    >
                        Cerrar
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog
                open={showDeleteDialog}
                onClose={handleDeleteDialogClose}
                maxWidth="sm"
                PaperProps={{
                    sx: {
                        borderRadius: 2,
                    }
                }}
            >
                <DialogTitle sx={{ pb: 1 }}>
                    Confirmar Eliminación
                </DialogTitle>
                <DialogContent sx={{ px: 3, py: 2 }}>
                    <Typography variant="body1" mb={2}>
                        ¿Estás seguro de que deseas eliminar el vehículo con placa <strong>{selectedVehicle?.license_plate}</strong>?
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Esta acción no se puede deshacer y se eliminará toda la información relacionada con este vehículo.
                    </Typography>
                    
                    {vehicleStore.error && (
                        <Alert severity="error" sx={{ mt: 2 }}>
                            {vehicleStore.error}
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button
                        onClick={handleDeleteDialogClose}
                        disabled={vehicleStore.isSubmitting}
                        sx={{
                            borderRadius: 1.5,
                            px: 3,
                            py: 1.5,
                            textTransform: 'none',
                            fontWeight: 500,
                        }}
                    >
                        Cancelar
                    </Button>
                    <Button
                        onClick={handleConfirmDelete}
                        variant="contained"
                        color="error"
                        disabled={vehicleStore.isSubmitting}
                        sx={{
                            borderRadius: 1.5,
                            px: 3,
                            py: 1.5,
                            textTransform: 'none',
                            fontWeight: 500,
                        }}
                    >
                        {vehicleStore.isSubmitting ? (
                            <>
                                <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />
                                Eliminando...
                            </>
                        ) : (
                            'Eliminar'
                        )}
                    </Button>
                </DialogActions>
            </Dialog>
            <Snackbar
                open={showSuccessMessage}
                autoHideDuration={4000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity="success"
                    variant="filled"
                >
                    {successMessage}
                </Alert>
            </Snackbar>
        </DashboardLayout>
    );
});

export default Vehicles;
